import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen,
  Trophy,
  Flame,
  TrendingUp,
  Brain,
  Gamepad2,
  Sparkles,
  Volume2,
  Star,
  MessageCircle,
  Rocket,
  Heart,
  ChevronRight,
  Send,
  X,
  Loader2,
  Library,
  Shuffle,
  Activity,
  Building,
  List,
  Scan,
  Puzzle,
  Headphones,
  Timer,
  Layers,
  LayoutGrid,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { type CarouselApi, Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { callGemini } from "@/lib/gemini";
import { getStoredGeminiKeys } from "@/lib/apiKeys";
import { supabase } from "@/lib/supabase";
import { I18N_LANGUAGE_KEYS, LANGUAGE_STORAGE_KEY } from "@/i18n/i18n";
import { toast } from "sonner";

const glass =
  "bg-white/[0.08] backdrop-blur-[16px] border border-white/[0.12]";
const heroBg =
  "bg-gradient-to-br from-violet-900 via-purple-800 to-fuchsia-600";
const gradientText =
  "bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent";
const cardHover =
  "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-3 hover:scale-[1.03] hover:shadow-[0_25px_50px_-12px_rgba(192,38,211,0.25)]";

type ChatMessage = { id: string; role: "user" | "bot"; content: string; isLoading?: boolean };

const TESTIMONIALS = [
  { textKey: "landing.testimonials.1.text", authorKey: "landing.testimonials.1.authorName", roleKey: "landing.testimonials.1.role", countryKey: "landing.testimonials.1.country", avatarUrl: "https://randomuser.me/api/portraits/women/1.jpg" },
  { textKey: "landing.testimonials.2.text", authorKey: "landing.testimonials.2.authorName", roleKey: "landing.testimonials.2.role", countryKey: "landing.testimonials.2.country", avatarUrl: "https://randomuser.me/api/portraits/men/1.jpg" },
  { textKey: "landing.testimonials.3.text", authorKey: "landing.testimonials.3.authorName", roleKey: "landing.testimonials.3.role", countryKey: "landing.testimonials.3.country", avatarUrl: "https://randomuser.me/api/portraits/men/2.jpg" },
  { textKey: "landing.testimonials.4.text", authorKey: "landing.testimonials.4.authorName", roleKey: "landing.testimonials.4.role", countryKey: "landing.testimonials.4.country", avatarUrl: "https://randomuser.me/api/portraits/women/2.jpg" },
  { textKey: "landing.testimonials.5.text", authorKey: "landing.testimonials.5.authorName", roleKey: "landing.testimonials.5.role", countryKey: "landing.testimonials.5.country", avatarUrl: "https://randomuser.me/api/portraits/men/3.jpg" },
  { textKey: "landing.testimonials.6.text", authorKey: "landing.testimonials.6.authorName", roleKey: "landing.testimonials.6.role", countryKey: "landing.testimonials.6.country", avatarUrl: "https://randomuser.me/api/portraits/women/3.jpg" },
  { textKey: "landing.testimonials.7.text", authorKey: "landing.testimonials.7.authorName", roleKey: "landing.testimonials.7.role", countryKey: "landing.testimonials.7.country", avatarUrl: "https://randomuser.me/api/portraits/men/4.jpg" },
  { textKey: "landing.testimonials.8.text", authorKey: "landing.testimonials.8.authorName", roleKey: "landing.testimonials.8.role", countryKey: "landing.testimonials.8.country", avatarUrl: "https://randomuser.me/api/portraits/men/5.jpg" },
  { textKey: "landing.testimonials.9.text", authorKey: "landing.testimonials.9.authorName", roleKey: "landing.testimonials.9.role", countryKey: "landing.testimonials.9.country", avatarUrl: "https://randomuser.me/api/portraits/women/4.jpg" },
  { textKey: "landing.testimonials.10.text", authorKey: "landing.testimonials.10.authorName", roleKey: "landing.testimonials.10.role", countryKey: "landing.testimonials.10.country", avatarUrl: "https://randomuser.me/api/portraits/women/5.jpg" },
];

const GAME_MODES: { nameKey: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { nameKey: "landing.games.shuffle", Icon: Shuffle },
  { nameKey: "landing.games.catMouse", Icon: Activity },
  { nameKey: "landing.games.spaceDefender", Icon: Rocket },
  { nameKey: "landing.games.towerLogic", Icon: Building },
  { nameKey: "landing.games.memoryJumble", Icon: List },
  { nameKey: "landing.games.memoryMix", Icon: Scan },
  { nameKey: "landing.games.rebusCipher", Icon: Puzzle },
  { nameKey: "landing.games.oddOneOut", Icon: Brain },
  { nameKey: "landing.games.soundClouds", Icon: Headphones },
  { nameKey: "landing.games.blitz", Icon: Timer },
  { nameKey: "landing.games.swipeArena", Icon: Layers },
  { nameKey: "landing.games.secretMosaic", Icon: LayoutGrid },
];

const MARQUEE_SET_WIDTH_PX = 12 * 200 + 11 * 24;

const SUPPORT_SYSTEM_PROMPT = `You are the official support and sales assistant for StudyDo, a premium AI-powered flashcard and language learning app. Be polite, helpful, and concise. Answer in the same language the user writes in.`;

/** Display order for the language switcher (12 languages) */
const LANDING_LANGUAGE_ORDER: string[] = [
  "en",
  "es",
  "fr",
  "uk",
  "ru",
  "de",
  "it",
  "nl",
  "pl",
  "pt-BR",
  "tr",
  "ko",
];

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  uk: "Українська",
  ru: "Русский",
  de: "Deutsch",
  it: "Italiano",
  nl: "Nederlands",
  pl: "Polski",
  "pt-BR": "Português (BR)",
  tr: "Türkçe",
  ko: "한국어",
};

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "bot",
      content: "",
    },
  ]);
  const [chatInput, setChatInput] = useState("");
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const demoVideoRef = useRef<HTMLDivElement>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [testimonialsApi, setTestimonialsApi] = useState<CarouselApi>();
  const [testimonialsIndex, setTestimonialsIndex] = useState(0);

  const geminiKeys = getStoredGeminiKeys();
  const supportUserName =
    profile?.username ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    "Guest";

  useEffect(() => {
    setMessages([
      {
        id: "welcome",
        role: "bot",
        content: t("landing.chatbot.greeting"),
      },
    ]);
  }, [t]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!testimonialsApi) return;
    const onSelect = () => setTestimonialsIndex(testimonialsApi.selectedScrollSnap());
    onSelect();
    testimonialsApi.on("select", onSelect);
    return () => testimonialsApi.off("select", onSelect);
  }, [testimonialsApi]);

  useEffect(() => {
    if (!testimonialsApi) return;
    const interval = setInterval(() => {
      testimonialsApi.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonialsApi]);

  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text) return;

    setChatInput("");
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);

    const botId = `b-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: botId, role: "bot", content: "", isLoading: true },
    ]);

    try {
      const history = messages
        .filter((m) => m.content && !m.isLoading)
        .concat([userMsg])
        .map((m) => (m.role === "user" ? `User: ${m.content}` : `Assistant: ${m.content}`))
        .join("\n");
      const prompt = history ? `${history}\nAssistant:` : `User: ${text}\nAssistant:`;

      const reply = await callGemini(
        prompt,
        geminiKeys,
        t("landing.chatbot.errorGeneric"),
        {
          systemInstruction: SUPPORT_SYSTEM_PROMPT,
          maxOutputTokens: 800,
          temperature: 0.7,
        }
      );
      const cleanReply = reply.trim();

      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId ? { ...m, content: cleanReply, isLoading: false } : m
        )
      );

      void supabase
        .from("ai_support_logs")
        .insert({
          user_name: supportUserName,
          question: text,
          answer: cleanReply,
        })
        .then(({ error }) => {
          if (error) {
            console.warn("[LandingPage] Failed to log AI support chat:", error);
          }
        });
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? { ...m, content: t("landing.chatbot.errorMessage"), isLoading: false }
            : m
        )
      );
    }
  };

  const submitFeedback = async () => {
    if (feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    try {
      const { error } = await supabase.from("feedback").insert({
        name: feedbackName || null,
        rating: feedbackRating,
        message: feedbackMessage.trim(),
        status: "pending",
        is_published: false,
      });
      if (error) throw error;
      setFeedbackSent(true);
      setFeedbackName("");
      setFeedbackRating(4);
      setFeedbackMessage("");
      toast.success(t("landing.feedback.thankYouModeration"));
    } catch (err) {
      console.error("Supabase Insert Error:", err);
      toast.error(t("landing.feedback.submitError"));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const scrollToDemo = () => {
    demoVideoRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-zinc-950 text-white">
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-500 text-white shrink-0">
              <BookOpen className="h-4 w-4" />
            </div>
            <span className="text-xl font-bold tracking-tighter">StudyDo</span>
          </div>
          <div className="flex flex-1 items-center justify-end gap-1 min-w-0 sm:gap-2">
            <div className="hidden md:flex items-center gap-3 text-sm font-medium shrink-0">
              <a href="#features" className="whitespace-nowrap py-2 px-2 transition hover:text-purple-400">
                {t("landing.nav.features")}
              </a>
              <a href="#games" className="whitespace-nowrap py-2 px-2 transition hover:text-purple-400">
                {t("landing.nav.gameModes")}
              </a>
              <a href="#reviews" className="whitespace-nowrap py-2 px-2 transition hover:text-purple-400">
                {t("landing.nav.reviews")}
              </a>
              <a href="#faq" className="whitespace-nowrap py-2 px-2 transition hover:text-purple-400">
                {t("landing.nav.faq")}
              </a>
            </div>
            <div className="flex items-center gap-1 shrink-0 sm:gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex h-9 min-w-[36px] items-center justify-center gap-1.5 rounded-xl px-2.5 py-2 text-sm font-medium transition hover:bg-white/10"
                    aria-label={t("landing.nav.language")}
                  >
                    <Globe className="h-4 w-4" />
                    <span className="hidden sm:inline text-xs">{LANGUAGE_NAMES[i18n.language] ?? i18n.language}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px] bg-zinc-900 border-white/10">
                  {LANDING_LANGUAGE_ORDER.map((code) => (
                    <DropdownMenuItem
                      key={code}
                      onClick={() => {
                        i18n.changeLanguage(code);
                        localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
                      }}
                      className={`cursor-pointer ${i18n.language === code ? "bg-purple-500/20 text-purple-300" : ""}`}
                    >
                      {LANGUAGE_NAMES[code] ?? code}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                className="flex h-9 min-w-[36px] items-center justify-center rounded-xl px-2.5 py-2 text-sm font-medium transition hover:bg-white/10"
              >
                <span className="hidden sm:inline">{t("landing.nav.support")}</span>
                <MessageCircle className="h-4 w-4 sm:hidden" />
              </button>
              <Link
                to="/auth"
                className="flex h-9 items-center justify-center rounded-xl bg-white px-3 py-2 font-semibold text-zinc-900 text-sm transition hover:bg-purple-400 hover:text-white sm:px-4"
              >
                {t("landing.nav.signUp")}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <section className={`flex min-h-screen items-center pt-20 pb-12 sm:pb-0 ${heroBg}`}>
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:gap-12 sm:px-6 md:grid-cols-2 md:items-center">
          <div className="space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 rounded-3xl bg-white/10 px-4 py-2 text-sm backdrop-blur-md">
              <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
              <span>{t("landing.hero.badge")}</span>
            </div>
            <h1 className="text-4xl font-bold leading-tight tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl">
              {t("landing.hero.titleLine1")}{" "}
              <span className={gradientText}>{t("landing.hero.titleHighlight")}</span>
              <br />
              {t("landing.hero.titleLine2")}
            </h1>
            <p className="max-w-lg text-base text-zinc-300 sm:text-lg md:text-xl">
              {t("landing.hero.subline")}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <Link
                to="/auth"
                className="studydo-cta-attention group flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-3xl bg-white px-6 py-4 text-base font-semibold text-zinc-900 transition hover:scale-105 sm:min-w-0 sm:px-10 sm:py-6 sm:text-lg"
              >
                {t("landing.hero.cta")}
                <ChevronRight className="studydo-cta-attention__icon h-6 w-6 shrink-0 sm:h-8 sm:w-8" />
              </Link>
              <button
                type="button"
                onClick={scrollToDemo}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-3xl border border-white/40 px-6 py-4 font-semibold text-base transition hover:bg-white/10 sm:min-w-0 sm:px-10 sm:py-6 sm:text-lg"
              >
                {t("landing.hero.watchDemo")}
              </button>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-zinc-400 sm:gap-8">
              <div>{t("landing.hero.check1")}</div>
              <div>{t("landing.hero.check2")}</div>
              <div>{t("landing.hero.check3")}</div>
            </div>
          </div>
          <div className="relative">
            <img
              src="/images/hero-app-screenshot.png"
              alt="StudyDo App"
              width={2048}
              height={2048}
              fetchPriority="high"
              loading="eager"
              decoding="sync"
              className="w-full max-w-[320px] rounded-3xl border border-white/10 shadow-2xl sm:max-w-none"
            />
            <div className="absolute -bottom-4 -left-2 max-w-[200px] rounded-2xl bg-zinc-900 p-4 font-medium shadow-xl sm:-bottom-6 sm:-left-6 sm:max-w-[240px] sm:rounded-3xl sm:p-6">
              <div className="mb-3 flex gap-2 text-purple-300">
                <Trophy className="h-8 w-8" />
                <Flame className="h-8 w-8" />
                <TrendingUp className="h-8 w-8" />
              </div>
              <p>{t("landing.hero.streakCard.days")}</p>
              <p className="text-sm text-emerald-400">{t("landing.hero.streakCard.rank")}</p>
            </div>
          </div>
        </div>
      </section>

      <div ref={demoVideoRef} className="border-b border-white/10 bg-zinc-900 py-6">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-6 px-4 opacity-75 sm:gap-12 sm:px-6">
          <div className="text-sm font-medium">Oxford</div>
          <div className="text-sm font-medium">Stanford</div>
          <div className="text-sm font-medium">HSE</div>
          <div className="text-sm font-medium">{t("landing.strip.students")}</div>
          <div className="flex items-center gap-1 text-sm font-medium">
            4.98 <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      </div>

      <section id="features" className="bg-zinc-950 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-12 text-center sm:mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl md:text-5xl">{t("landing.features.title")}</h2>
            <p className="mt-3 text-base text-zinc-400 sm:mt-4 sm:text-lg md:text-xl">{t("landing.features.subtitle")}</p>
          </div>
          <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-500/20">
                <Brain className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.sm2.title")}</h3>
              <p className="text-zinc-400">{t("landing.features.sm2.desc")}</p>
            </div>
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-500/20">
                <Gamepad2 className="h-7 w-7 text-pink-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.games.title")}</h3>
              <p className="text-zinc-400">{t("landing.features.games.desc")}</p>
            </div>
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-500/20">
                <Sparkles className="h-7 w-7 text-cyan-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.ai.title")}</h3>
              <p className="text-zinc-400">{t("landing.features.ai.desc")}</p>
            </div>
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/20">
                <Volume2 className="h-7 w-7 text-emerald-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.tts.title")}</h3>
              <p className="text-zinc-400">{t("landing.features.tts.desc")}</p>
            </div>
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/20">
                <Trophy className="h-7 w-7 text-amber-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.leaderboard_title", "Глобальный рейтинг")}</h3>
              <p className="text-zinc-400">{t("landing.features.leaderboard_desc", "Зарабатывай кристаллы, поддерживай стрик и соревнуйся с тысячами учеников по всему миру.")}</p>
            </div>
            <div className={`rounded-3xl p-6 sm:p-8 ${glass} ${cardHover}`}>
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-500/20">
                <Library className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold">{t("landing.features.library_title", "Умная библиотека")}</h3>
              <p className="text-zinc-400">{t("landing.features.library_desc", "Структурируй знания по папкам, объединяй модули в один клик и держи всё под контролем.")}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="games" className="overflow-hidden bg-zinc-900 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="mb-12 text-center text-3xl font-bold sm:mb-16 sm:text-4xl md:text-5xl">{t("landing.games.title")}</h2>
          <div className="overflow-hidden">
            <motion.div
              className="flex gap-6"
              style={{ width: MARQUEE_SET_WIDTH_PX * 2 }}
              animate={{ x: -MARQUEE_SET_WIDTH_PX }}
              transition={{
                repeat: Infinity,
                repeatType: "loop",
                duration: 45,
                ease: "linear",
              }}
            >
              {[1, 2].map((set) => (
                <div
                  key={set}
                  className="flex flex-shrink-0 gap-6"
                  style={{ width: MARQUEE_SET_WIDTH_PX }}
                >
                  {GAME_MODES.map(({ nameKey, Icon }) => (
                    <div
                      key={`${set}-${nameKey}`}
                      className={`flex min-w-[200px] flex-shrink-0 flex-col items-center justify-center rounded-2xl p-6 text-center ${glass} transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.35)]`}
                    >
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20">
                        <Icon className="h-6 w-6 text-purple-400" />
                      </div>
                      <span className="font-medium text-white">{t(nameKey)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section id="reviews" className="bg-zinc-950 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl overflow-hidden px-4 sm:px-6">
          <h2 className="mb-4 text-center text-3xl font-bold sm:text-4xl md:text-5xl">{t("landing.reviews.title")}</h2>
          <p className="mb-10 text-center text-base text-zinc-400 sm:mb-12 sm:text-lg">{t("landing.reviews.subtitle")}</p>

          <Carousel
            setApi={setTestimonialsApi}
            opts={{ loop: true, align: "start" }}
            className="relative mx-auto w-full max-w-[calc(100vw-2rem)] sm:max-w-md md:max-w-4xl"
          >
            <CarouselContent className="-ml-4">
              {TESTIMONIALS.map((item) => (
                <CarouselItem key={item.textKey} className="basis-full pl-4 md:basis-1/2">
                  <div className="mx-auto max-w-sm rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl sm:p-6">
                    <div className="mb-4 flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="break-words italic text-zinc-200">"{t(item.textKey)}"</p>
                    <div className="mt-6 flex items-center gap-3">
                      <img
                        src={item.avatarUrl}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-white">{t(item.authorKey)}</div>
                        <div className="text-xs text-zinc-500">
                          {t(item.roleKey)} · {t(item.countryKey)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-10 flex justify-center gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => testimonialsApi?.scrollTo(i)}
                  className={`rounded-full transition-all ${
                    testimonialsIndex === i
                      ? "h-3 w-8 bg-purple-500 sm:h-2"
                      : "h-3 w-3 bg-white/30 hover:bg-white/50 sm:h-2 sm:w-2"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Link to="/reviews">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/20 bg-transparent text-zinc-300 hover:bg-white/10 hover:text-white"
                >
                  {t("landing.reviews.seeAll")}
                </Button>
              </Link>
            </div>
          </Carousel>
        </div>
      </section>

      <section className="bg-zinc-900 py-16 sm:py-24">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
          <h2 className="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl">{t("landing.feedback.title")}</h2>
          <p className="mb-10 break-words text-base text-zinc-400 sm:text-lg">{t("landing.feedback.subtitle")}</p>

          {feedbackSent ? (
            <div className={`rounded-3xl p-10 ${glass}`}>
              <p className="font-medium text-white">{t("landing.feedback.thankYou")}</p>
              <p className="mt-2 text-sm text-zinc-500">{t("landing.feedback.sentNote")}</p>
            </div>
          ) : (
            <form
              className={`space-y-8 rounded-3xl p-10 ${glass}`}
              onSubmit={(e) => {
                e.preventDefault();
                submitFeedback();
              }}
            >
              <div>
                <label className="mb-2 block text-sm">{t("landing.feedback.name")}</label>
                <input
                  type="text"
                  className="w-full rounded-2xl border border-white/20 bg-white/5 px-6 py-4 focus:border-purple-400 focus:outline-none"
                  placeholder={t("landing.feedback.namePlaceholder")}
                  value={feedbackName}
                  onChange={(e) => setFeedbackName(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm">{t("landing.feedback.rating")}</label>
                <div className="flex justify-center gap-1 sm:gap-2">
                  {[1, 2, 3, 4, 5].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setFeedbackRating(r)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-yellow-400 transition hover:opacity-80"
                      aria-label={t("landing.feedback.ratingLabel", { count: r })}
                    >
                      <Star
                        className={`h-8 w-8 sm:h-10 sm:w-10 ${feedbackRating >= r ? "fill-current" : "opacity-40"}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm">{t("landing.feedback.message")}</label>
                <textarea
                  className="h-32 w-full rounded-3xl border border-white/20 bg-white/5 px-6 py-5 focus:border-purple-400 focus:outline-none"
                  placeholder={t("landing.feedback.messagePlaceholder")}
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={feedbackSubmitting}
                className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-3xl bg-gradient-to-r from-purple-600 to-pink-500 py-4 text-base font-semibold disabled:cursor-not-allowed disabled:opacity-70 sm:py-6 sm:text-lg"
              >
                {feedbackSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {t("landing.feedback.submitting")}
                  </>
                ) : (
                  t("landing.feedback.submit")
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <section id="faq" className="bg-zinc-950 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-3xl font-bold sm:mb-12 sm:text-4xl md:text-5xl">{t("landing.faq.title")}</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {([1, 2, 3, 4, 5, 6, 7] as const).map((i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className={`rounded-3xl px-4 sm:px-6 ${glass} border-none`}
              >
                <AccordionTrigger className="break-words py-5 text-left text-base font-medium text-white hover:no-underline hover:text-purple-300 sm:py-6 sm:text-lg">
                  {t(`landing.faq.q${i}`)}
                </AccordionTrigger>
                <AccordionContent className="break-words pb-5 text-sm text-zinc-400 sm:pb-6 sm:text-base">
                  {t(`landing.faq.a${i}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-gradient-to-b from-transparent via-purple-950 to-transparent py-20 sm:py-32">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="break-words text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl">{t("landing.cta.title")}</h2>
          <p className="mt-4 break-words text-base text-zinc-300 sm:mt-6 sm:text-lg md:text-xl lg:text-2xl">{t("landing.cta.subtitle")}</p>
          <Link
            to="/auth"
            className="studydo-cta-attention group mt-8 inline-flex min-h-[44px] items-center justify-center gap-3 rounded-3xl bg-white px-8 py-5 text-lg font-bold text-zinc-950 transition hover:scale-110 sm:mt-12 sm:px-12 sm:py-6 sm:text-xl md:px-16 md:py-8 md:text-2xl"
          >
            {t("landing.cta.button")}
            <Rocket className="studydo-cta-attention__icon h-10 w-10" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-black py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-6 flex justify-center gap-6 text-sm">
            <Link to="/reviews" className="text-zinc-400 transition hover:text-purple-400">
              {t("landing.footer.reviews")}
            </Link>
          </div>
          <div className="text-center text-sm text-zinc-500">
            {t("landing.footer.copyright")}{" "}
            <span className="inline-flex items-center gap-1">
              {t("landing.footer.madeWith")} <Heart className="inline h-4 w-4 fill-red-500 text-red-500" /> {t("landing.footer.forLearners")}
            </span>
          </div>
        </div>
      </footer>

      <button
        type="button"
        onClick={() => setChatOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 flex h-14 min-h-[44px] min-w-[44px] w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-500 shadow-2xl transition hover:scale-110 sm:bottom-8 sm:right-8 sm:h-16 sm:w-16"
        aria-label={t("landing.chatbot.toggle")}
      >
        <MessageCircle className="h-8 w-8 text-white" />
      </button>

      {chatOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-hidden bg-black/70 md:items-center"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-[90dvh] w-full max-w-[100vw] flex-col overflow-hidden rounded-t-3xl bg-zinc-900 md:max-h-[85vh] md:h-[620px] md:w-[420px] md:max-w-[calc(100vw-2rem)] md:rounded-3xl">
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-zinc-800 p-4 sm:p-5">
              <div className="min-w-0 flex-1 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{t("landing.chatbot.title")}</div>
                  <div className="truncate text-xs text-emerald-400">{t("landing.chatbot.subtitle")}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setChatOpen(false)}
                className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-xl text-white transition hover:bg-white/10 hover:opacity-90"
                aria-label={t("landing.chatbot.close")}
              >
                <X className="h-6 w-6 sm:h-8 sm:w-8" />
              </button>
            </div>

            <div
              ref={chatScrollRef}
              className="min-h-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden p-4 text-sm sm:p-6"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`max-w-[85%] break-words rounded-3xl p-4 ${
                    msg.role === "user"
                      ? "ml-auto bg-[#5B21B6]"
                      : "rounded-tl-none bg-white/10"
                  }`}
                >
                  {msg.isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("landing.chatbot.thinking")}
                    </span>
                  ) : (
                    msg.content
                  )}
                </div>
              ))}
            </div>

            <div className="shrink-0 border-t border-white/10 bg-zinc-800 p-4">
              <div className="flex gap-2 sm:gap-3">
                <input
                  type="text"
                  placeholder={t("landing.chatbot.placeholder")}
                  className="flex-1 rounded-3xl border border-white/20 bg-zinc-900 px-6 py-4 text-sm focus:border-purple-400 focus:outline-none"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendChatMessage();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={sendChatMessage}
                  className="flex h-12 min-w-[44px] flex-shrink-0 items-center justify-center rounded-3xl bg-purple-600 text-white transition hover:bg-purple-700"
                  aria-label={t("landing.chatbot.send")}
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-3 text-center text-[10px] text-zinc-500">
                {t("landing.chatbot.disclaimer")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
